
import './query_limit_notice';
import React from 'react';

/*
 * BLDB Search Header Plugin
 * SequenceServer 3.1.3
 *
 * Layout:
 *
 * [BLDB logo] [Orange title panel] [BLDB logo]
 *
 * Below:
 * - Genome submission warning
 * - Full BLDB publication citation
 *
 * This plugin is independent of SequenceServer core files.
 */

export const SearchHeaderPlugin = () => (
  <section
    className="bldb-search-header"
    aria-label="BLDB BLAST information"
  >
    <style>{`
      /* Main header container */

      .bldb-search-header {
        max-width: 1080px;
        margin: 12px auto 24px;
        font-family: Arial, Helvetica, sans-serif;
        color: #273342;
      }

      /* Header layout */

      .bldb-banner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 14px;
        padding: 4px 0;
        background: #FFFFFF;
      }

      /* BLDB logos on both sides */

      .bldb-logo {
        display: block;
        width: 58px;
        height: 74px;
        object-fit: contain;
        flex-shrink: 0;
      }

      /* Orange title panel */

      .bldb-title-panel {
        flex: 1;
        min-width: 0;
        background: #FFCC00;
        border: 1px solid #E6B800;
        border-radius: 8px;
        padding: 7px 10px;
      }

      /* SVG title and subtitle */

      .bldb-banner-svg {
        display: block;
        width: 100%;
        height: auto;
        max-height: 70px;
      }

      /* Genome submission warning */

      .bldb-warning {
        margin-top: 16px;
        padding: 12px 16px;
        border-left: 4px solid #C58A36;
        background: #FAF7F1;
        border-radius: 4px;
        line-height: 1.5;
      }

      .bldb-warning strong {
        font-weight: 700;
      }

      .bldb-warning p {
        margin: 5px 0 0;
      }

      /* Scientific citation */

      .bldb-citation {
        margin-top: 14px;
        font-size: 14px;
        line-height: 1.6;
      }

      .bldb-citation strong {
        font-weight: 700;
      }

      .bldb-citation p {
        margin: 5px 0 0;
      }

      .bldb-citation a {
        color: #1766A0;
        text-decoration: underline;
        overflow-wrap: anywhere;
      }

      .bldb-citation a:hover {
        color: #104A75;
      }

      /* Responsive layout */

      @media (max-width: 700px) {
        .bldb-search-header {
          margin: 10px auto 18px;
        }

        .bldb-banner {
          gap: 7px;
          padding: 3px 0;
        }

        .bldb-logo {
          width: 36px;
          height: 46px;
        }

        .bldb-title-panel {
          padding: 5px 6px;
          border-radius: 6px;
        }

        .bldb-banner-svg {
          max-height: 54px;
        }

        .bldb-warning {
          margin-top: 12px;
          padding: 10px 12px;
        }

        .bldb-citation {
          margin-top: 12px;
          font-size: 13px;
        }
      }
    `}</style>

    {/* BLDB logos and orange title */}

    <div className="bldb-banner">

      <img
        className="bldb-logo"
        src="/BLDB_logo.png"
        alt="Beta-Lactamase DataBase logo"
      />

      <div className="bldb-title-panel">
        <svg
          className="bldb-banner-svg"
          viewBox="0 0 700 70"
          role="img"
          aria-label="Beta-Lactamase DataBase - Structure and function"
        >
          <text
            x="350"
            y="29"
            textAnchor="middle"
            fontSize="29"
            fontFamily="Arial, Helvetica, sans-serif"
            fill="#161616"
          >
            <tspan fill="#BE0000" fontWeight="bold">B</tspan>
            eta-
            <tspan fill="#BE0000" fontWeight="bold">L</tspan>
            actamase{' '}
            <tspan fill="#BE0000" fontWeight="bold">D</tspan>
            ata
            <tspan fill="#BE0000" fontWeight="bold">B</tspan>
            ase
          </text>

          <text
            x="350"
            y="54"
            textAnchor="middle"
            fontSize="19"
            fontFamily="Arial, Helvetica, sans-serif"
            fill="#161616"
          >
            Structure and function
          </text>
        </svg>
      </div>

      <img
        className="bldb-logo"
        src="/BLDB_logo.png"
        alt=""
        aria-hidden="true"
      />

    </div>

    {/* Warning against whole-genome submissions */}

    <div className="bldb-warning" role="note">
      <strong>
        Please do not submit complete genomes.
      </strong>

      <p>
        This BLAST server is intended for individual nucleotide
        or protein sequences. Please avoid submitting complete
        genomes or very large sequence datasets, as these
        searches can consume substantial computational resources
        and block the website.
      </p>
    </div>

    {/* BLDB scientific citation */}

    <div className="bldb-citation">
      <strong>How to cite BLDB BLAST</strong>

      <p>
        Naas, T.; Oueslati, S.; Bonnin, R. A.;
        Dabos, M. L.; Zavala, A.; Dortet, L.;
        Retailleau, P.; Iorga, B. I.
        {' '}Beta-Lactamase DataBase (BLDB) –
        Structure and function.
        {' '}<em>J. Enzyme Inhib. Med. Chem.</em>
        {' '}<strong>2017</strong>, <em>32</em>,
        {' '}917–919.
        {' '}
        <a
          href="https://doi.org/10.1080/14756366.2017.1344235"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://doi.org/10.1080/14756366.2017.1344235
        </a>
      </p>
    </div>

  </section>
);
